/**
 * Trip Export Service
 *
 * Handles complete trip export as ZIP archives with all related data and media
 * Integrates with all domain services to gather comprehensive trip information
 */

import zipProcessor from '../formats/zip';

class TripExportService {
  constructor() {
    this.maxExportSize = 100 * 1024 * 1024; // 100MB limit
    this.notifications = {
      started: null,
      completed: null,
      error: null
    };
  }

  /**
   * Export complete trip as ZIP archive
   *
   * @param {Object} context - Current application context
   * @param {Object} options - Export options
   * @returns {Promise<boolean>} Success status
   */
  /**
   * Export complete trip - Delegates to TripExportController
   * This is a lightweight wrapper that should be replaced with controller injection
   *
   * @deprecated Use TripExportController.exportTrip() directly instead
   */
  async exportCompleteTrip(context, options = {}) {
    try {
      console.log('[TripExportService] Delegating to TripExportController...');

      // Dynamically import the controller to avoid circular dependencies
      const { default: tripExportController } = await import('../../../../main-app/src/controllers/TripExportController');

      // Delegate to the controller which has the complete implementation
      return await tripExportController.exportTrip(context, options);

    } catch (error) {
      console.error('[TripExportService] Export failed:', error);
      this.showNotification('error', error.message);
      return false;
    }
  }

  /**
   * Get trip summary for display
   *
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Trip summary
   */
  async getTripSummary(context) {
    try {
      const trip = context.trip || {};
      const summary = {
        name: trip.name || 'My Trip',
        destination: trip.destination || 'Unknown Destination',
        days: await this.getDaysCount(context),
        travelers: trip.travelers || 1,
        dateRange: this.formatDateRange(trip.startDate, trip.endDate),
        startDate: trip.startDate,
        endDate: trip.endDate
      };

      return summary;

    } catch (error) {
      console.error('Failed to get trip summary:', error);
      return null;
    }
  }

  /**
   * Get export size estimate
   *
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Export estimate
   */
  async getExportEstimate(context) {
    try {
      const tripData = await this.gatherTripData(context);

      // Calculate component sizes
      const dataSize = this.calculateJsonDataSize(tripData);
      const csvSize = this.calculateCsvSize(tripData);
      const documentSize = this.calculateDocumentSize(tripData.documents || []);
      const mediaSize = this.calculateMediaSize(tripData.documents || []);

      const totalItems = this.calculateTotalItems(tripData);
      const totalSize = dataSize + csvSize + documentSize + mediaSize;

      return {
        totalItems,
        totalSize,
        dataFiles: 6, // trip-info, days, places, accommodation, travel, documents
        dataSize,
        csvSize,
        documentCount: tripData.documents?.filter(doc => !this.isImageFile(doc)).length || 0,
        documentSize,
        mediaCount: tripData.documents?.filter(doc => this.isImageFile(doc)).length || 0,
        mediaSize
      };

    } catch (error) {
      console.error('Failed to generate export estimate:', error);
      return {
        totalItems: 0,
        totalSize: 0,
        dataFiles: 0,
        dataSize: 0,
        csvSize: 0,
        documentCount: 0,
        documentSize: 0,
        mediaCount: 0,
        mediaSize: 0
      };
    }
  }

  /**
   * Gather comprehensive trip data from all services
   *
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Complete trip data
   */
  async gatherTripData(context) {
    try {
      console.log('[TripExport] Gathering complete trip data from Firebase...');

      // Use BackupServiceRegistry to fetch data from all registered services
      const { backupRegistry } = await import('@trip-planner-monorepo/shared');

      // Use effectiveUserId to support shared trips (owner's UID), falling back to current user
      const userId = context.effectiveUserId || context.user?.uid;
      const tripId = context.tripId;

      if (!userId || !tripId) {
        throw new Error('Missing userId or tripId in context');
      }

      console.log('[TripExport] Fetching data for user:', userId, 'trip:', tripId);

      // Get all registered services
      const services = backupRegistry.getAllServices();
      console.log('[TripExport] Found', services.length, 'registered services:',
        services.map(s => s.name).join(', '));

      // Fetch data from each service
      const serviceData = {};
      for (const service of services) {
        try {
          console.log('[TripExport] Fetching', service.name, 'data...');
          const data = await service.fetchAll(userId, tripId);

          if (data && (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0)) {
            serviceData[service.name] = data;
            console.log('[TripExport]', service.name, 'data fetched:',
              Array.isArray(data) ? data.length + ' items' : 'object');
          } else {
            console.log('[TripExport]', service.name, 'has no data');
          }
        } catch (error) {
          console.error('[TripExport] Error fetching', service.name, 'data:', error);
          // Continue with other services even if one fails
          serviceData[service.name] = [];
        }
      }

      // Structure the data according to expected format
      const tripData = {
        // Trip metadata
        trip: {
          id: tripId,
          name: context.trip?.name || 'My Trip',
          description: context.trip?.description || '',
          destination: context.trip?.destination || '',
          startDate: context.trip?.startDate || '',
          endDate: context.trip?.endDate || '',
          travelers: context.trip?.travelers || 1,
          budget: context.trip?.budget || 0,
          currency: context.trip?.currency || 'USD',
          status: context.trip?.status || 'planned',
          createdAt: context.trip?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          userId: userId
        },

        // Entity collections from registered services (with contentBlocks preserved)
        days: serviceData.days || [],
        places: serviceData.places || [],
        accommodation: serviceData.accommodation || [],
        travel: serviceData.travel || [],
        documents: serviceData.documents || [],

        // Export metadata
        exportMetadata: {
          exportDate: new Date().toISOString(),
          exportVersion: '1.0.0',
          exportedBy: context.user?.email || 'unknown',
          tripId: tripId,
          dataVersion: '2025.1',
          exportTool: 'Trip Planner',
          exportType: 'complete_trip'
        }
      };

      console.log('[TripExport] Trip data gathered successfully');
      console.log('[TripExport] Data summary:', {
        trip: tripData.trip.name,
        daysCount: tripData.days.length,
        placesCount: tripData.places.length,
        accommodationCount: tripData.accommodation.length,
        travelCount: tripData.travel.length,
        documentsCount: tripData.documents.length
      });

      return tripData;

    } catch (error) {
      console.error('[TripExport] Failed to gather trip data:', error);
      throw error;
    }
  }

  /**
   * Fetch actual document binary files from Firebase Storage
   *
   * @deprecated This method has been removed. Document fetching is now handled by TripExportController.
   * The controller's generateExportPackage() method includes the complete document fetching logic.
   */

  /**
   * Generate comprehensive mock trip data
   */
  generateComprehensiveTripData(context) {
    const trip = context.trip || {};
    const tripId = context.tripId || 'demo-trip';

    return {
      // Trip metadata
      trip: {
        id: tripId,
        name: trip.name || 'Demo Trip to Paris',
        description: trip.description || 'A wonderful journey through the City of Light',
        destination: trip.destination || 'Paris, France',
        startDate: trip.startDate || '2025-03-15',
        endDate: trip.endDate || '2025-03-20',
        travelers: trip.travelers || 2,
        budget: trip.budget || 5000,
        currency: trip.currency || 'EUR',
        status: 'planned',
        createdAt: '2025-01-15T10:00:00Z',
        updatedAt: new Date().toISOString(),
        userId: context.user?.uid || 'demo-user'
      },

      // Days and itinerary
      days: this.generateDaysData(trip),

      // Places and locations
      places: this.generatePlacesData(),

      // Accommodation details
      accommodation: this.generateAccommodationData(),

      // Travel arrangements
      travel: this.generateTravelData(),

      // Documents and files
      documents: this.generateDocumentsData(),

      // Export metadata
      exportMetadata: {
        exportDate: new Date().toISOString(),
        exportVersion: '1.0.0',
        exportedBy: context.user?.email || 'demo@example.com',
        tripId: tripId,
        dataVersion: '2025.1',
        exportTool: 'Trip Planner',
        exportType: 'complete_trip'
      }
    };
  }

  generateDaysData(trip) {
    const startDate = new Date(trip.startDate || '2025-03-15');
    const days = [];

    for (let i = 0; i < 6; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);

      days.push({
        id: `day-${i + 1}`,
        date: currentDate.toISOString().split('T')[0],
        dayNumber: i + 1,
        title: this.getDayTitle(i + 1),
        activities: this.getDayActivities(i + 1),
        notes: this.getDayNotes(i + 1),
        weather: this.getDayWeather(i + 1),
        budget: 180 + (i * 20)
      });
    }

    return days;
  }

  getDayTitle(dayNumber) {
    const titles = [
      'Arrival Day',
      'Paris Highlights',
      'Museum & Culture Day',
      'Versailles Day Trip',
      'Shopping & Relaxation',
      'Departure Day'
    ];
    return titles[dayNumber - 1] || `Day ${dayNumber}`;
  }

  getDayActivities(dayNumber) {
    const activities = [
      ['Airport arrival', 'Hotel check-in', 'Montmartre exploration'],
      ['Eiffel Tower visit', 'Seine river cruise', 'Louvre Museum'],
      ['Musée d\'Orsay', 'Latin Quarter walk', 'Notre-Dame area'],
      ['Palace of Versailles', 'Gardens tour', 'Return to Paris'],
      ['Champs-Élysées shopping', 'Arc de Triomphe', 'Café culture'],
      ['Final sightseeing', 'Souvenir shopping', 'Airport departure']
    ];
    return activities[dayNumber - 1] || [`Day ${dayNumber} activities`];
  }

  getDayNotes(dayNumber) {
    const notes = [
      'Take it easy on arrival day, adjust to time zone',
      'Book Eiffel Tower tickets in advance, wear comfortable shoes',
      'Museum pass recommended, check opening hours',
      'Full day trip, pack snacks and comfortable walking shoes',
      'Last chance for shopping, check store hours',
      'Departure day, confirm flight times and pack accordingly'
    ];
    return notes[dayNumber - 1] || `Notes for day ${dayNumber}`;
  }

  getDayWeather(dayNumber) {
    const weather = [
      'Partly cloudy, 12°C',
      'Sunny, 15°C',
      'Light rain, 10°C',
      'Cloudy, 13°C',
      'Sunny, 16°C',
      'Clear, 14°C'
    ];
    return weather[dayNumber - 1] || 'Check weather forecast';
  }

  generatePlacesData() {
    return [
      {
        id: 'place-1',
        name: 'Eiffel Tower',
        address: 'Champ de Mars, 5 Avenue Anatole France, 75007 Paris, France',
        type: 'landmark',
        coordinates: { lat: 48.8584, lng: 2.2945 },
        visitDate: '2025-03-16',
        duration: '2 hours',
        notes: 'Best views at sunset, book skip-the-line tickets',
        rating: 5,
        cost: 25
      },
      {
        id: 'place-2',
        name: 'Louvre Museum',
        address: 'Rue de Rivoli, 75001 Paris, France',
        type: 'museum',
        coordinates: { lat: 48.8606, lng: 2.3376 },
        visitDate: '2025-03-16',
        duration: '3 hours',
        notes: 'Must see Mona Lisa, arrive early to avoid crowds',
        rating: 5,
        cost: 17
      },
      {
        id: 'place-3',
        name: 'Palace of Versailles',
        address: 'Place d\'Armes, 78000 Versailles, France',
        type: 'palace',
        coordinates: { lat: 48.8049, lng: 2.1204 },
        visitDate: '2025-03-18',
        duration: '6 hours',
        notes: 'Full day trip, gardens are spectacular in spring',
        rating: 5,
        cost: 27
      }
    ];
  }

  generateAccommodationData() {
    return [
      {
        id: 'hotel-1',
        name: 'Hotel Le Marais Boutique',
        address: '12 Rue des Rosiers, 75004 Paris, France',
        type: 'hotel',
        checkIn: '2025-03-15',
        checkOut: '2025-03-21',
        nights: 6,
        guests: 2,
        roomType: 'Superior Double Room with City View',
        totalCost: 1200,
        currency: 'EUR',
        confirmationNumber: 'HLM123456789',
        contactPhone: '+33 1 42 72 34 23',
        contactEmail: 'reservations@hotelmarais.com',
        amenities: ['Free WiFi', 'Breakfast included', 'Air conditioning', 'Room service'],
        rating: 4.5,
        notes: 'Historic district location, walking distance to major attractions'
      }
    ];
  }

  generateTravelData() {
    return [
      {
        id: 'flight-1',
        type: 'flight',
        airline: 'Air France',
        flightNumber: 'AF123',
        departure: {
          airport: 'JFK',
          city: 'New York',
          country: 'United States',
          date: '2025-03-14',
          time: '20:30',
          terminal: '1',
          gate: 'A12'
        },
        arrival: {
          airport: 'CDG',
          city: 'Paris',
          country: 'France',
          date: '2025-03-15',
          time: '10:30',
          terminal: '2E',
          gate: 'L15'
        },
        duration: '7h 00m',
        seatNumber: '15A',
        cabinClass: 'Economy',
        bookingReference: 'AF7B8K9M',
        cost: 750,
        currency: 'EUR',
        baggage: '1 checked bag, 1 carry-on',
        meals: 'Dinner and breakfast included'
      },
      {
        id: 'flight-2',
        type: 'flight',
        airline: 'Air France',
        flightNumber: 'AF456',
        departure: {
          airport: 'CDG',
          city: 'Paris',
          country: 'France',
          date: '2025-03-21',
          time: '14:15',
          terminal: '2E',
          gate: 'K20'
        },
        arrival: {
          airport: 'JFK',
          city: 'New York',
          country: 'United States',
          date: '2025-03-21',
          time: '17:45',
          terminal: '1',
          gate: 'B08'
        },
        duration: '8h 30m',
        seatNumber: '12F',
        cabinClass: 'Economy',
        bookingReference: 'AF7B8K9M',
        cost: 750,
        currency: 'EUR',
        baggage: '1 checked bag, 1 carry-on',
        meals: 'Lunch included'
      }
    ];
  }

  generateDocumentsData() {
    return [
      {
        id: 'doc-1',
        name: 'Air France Flight Confirmation.pdf',
        type: 'booking_confirmation',
        category: 'travel',
        uploadDate: '2025-01-20T14:30:00Z',
        size: 245760, // ~240KB
        associatedWith: ['flight-1', 'flight-2'],
        summary: 'Round-trip flight booking confirmation with seat assignments',
        mimeType: 'application/pdf'
      },
      {
        id: 'doc-2',
        name: 'Hotel Le Marais Reservation.pdf',
        type: 'booking_confirmation',
        category: 'accommodation',
        uploadDate: '2025-01-22T09:15:00Z',
        size: 189440, // ~185KB
        associatedWith: ['hotel-1'],
        summary: 'Hotel reservation confirmation with room details and amenities',
        mimeType: 'application/pdf'
      },
      {
        id: 'doc-3',
        name: 'Paris Museum Pass.jpg',
        type: 'ticket',
        category: 'activities',
        uploadDate: '2025-02-05T11:20:00Z',
        size: 1048576, // ~1MB
        associatedWith: ['place-1', 'place-2'],
        summary: 'Digital museum pass for Paris attractions',
        mimeType: 'image/jpeg'
      },
      {
        id: 'doc-4',
        name: 'Versailles Ticket.pdf',
        type: 'ticket',
        category: 'activities',
        uploadDate: '2025-02-10T16:45:00Z',
        size: 156672, // ~153KB
        associatedWith: ['place-3'],
        summary: 'Versailles palace and gardens entry ticket',
        mimeType: 'application/pdf'
      }
    ];
  }

  /**
   * Helper calculation methods
   */
  calculateJsonDataSize(tripData) {
    const jsonString = JSON.stringify(tripData, null, 2);
    return jsonString.length; // Approximate byte count
  }

  calculateCsvSize(tripData) {
    // Rough estimate for CSV exports
    const rows = (tripData.days?.length || 0) +
                 (tripData.places?.length || 0) +
                 (tripData.accommodation?.length || 0) +
                 (tripData.travel?.length || 0);
    return rows * 200; // ~200 bytes per row average
  }

  calculateDocumentSize(documents) {
    return documents
      .filter(doc => !this.isImageFile(doc))
      .reduce((total, doc) => total + (doc.size || 0), 0);
  }

  calculateMediaSize(documents) {
    return documents
      .filter(doc => this.isImageFile(doc))
      .reduce((total, doc) => total + (doc.size || 0), 0);
  }

  calculateTotalItems(tripData) {
    return (tripData.days?.length || 0) +
           (tripData.places?.length || 0) +
           (tripData.accommodation?.length || 0) +
           (tripData.travel?.length || 0) +
           (tripData.documents?.length || 0);
  }

  isImageFile(doc) {
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    return imageTypes.includes(doc.mimeType);
  }

  /**
   * Utility methods
   */
  validateExportContext(context) {
    return !!(context.user && context.tripId && context.trip);
  }

  async estimateExportSize(tripData) {
    const jsonSize = this.calculateJsonDataSize(tripData);
    const documentSize = this.calculateDocumentSize(tripData.documents || []);
    const mediaSize = this.calculateMediaSize(tripData.documents || []);
    const overhead = 50000; // 50KB for ZIP structure, README, etc.

    return jsonSize + documentSize + mediaSize + overhead;
  }

  generateExportFilename(trip) {
    const tripName = (trip.name || 'trip')
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase();
    const date = new Date().toISOString().split('T')[0];
    return `${tripName}-export-${date}`;
  }

  formatDateRange(startDate, endDate) {
    if (!startDate || !endDate) return 'Date range not specified';

    const start = new Date(startDate);
    const end = new Date(endDate);
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    return `${startStr} - ${endStr}`;
  }

  async getDaysCount(context) {
    if (context.trip?.startDate && context.trip?.endDate) {
      const start = new Date(context.trip.startDate);
      const end = new Date(context.trip.endDate);
      return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    }
    return 6; // Default for demo
  }

  /**
   * Notification methods
   */
  showNotification(type, message = '') {
    const messages = {
      started: 'Starting complete trip export...',
      completed: 'Trip export completed successfully',
      error: `Trip export error: ${message}`
    };

    console.log(messages[type]);

    if (this.notifications[type]) {
      this.notifications[type](messages[type]);
    }
  }

  setNotificationHandlers(handlers) {
    this.notifications = { ...this.notifications, ...handlers };
  }
}

export default new TripExportService();